/**
 * PB-FOUNDATION offers plugin — stepActive listener, visible list, retry UX.
 * PB-FND-5: must not call queueStepNavigation / moveStep loops from render.
 * PB-CART-1: skip render on child-line cart_edit.
 */

import { CART_EDIT_FLOW, shouldSkipPluginOnChildCartEdit } from '@theme/pb-cart-child-edit';
import { buildOffersHtml, bindOffersListHandlers, areOfferRenderDepsReady } from '@theme/pb-offers-render';
import { ensureOfferRenderDepsReady } from '@theme/pb-entry-readiness';

export const OFFERS_ERROR_MESSAGE =
  "We couldn't load your offers right now. Please check your connection.";

export const OFFERS_RETRY_LABEL = 'Try again';

/** FNV-1a 32-bit hash for offer list HTML — skip innerHTML when unchanged. */
export function offersContentHash(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(16);
}

export function buildOffersErrorHtml(message, retryLabel) {
  const msg = message || OFFERS_ERROR_MESSAGE;
  const label = retryLabel || OFFERS_RETRY_LABEL;
  return (
    '<div class="pb-bundle-list pb-offers-error" role="alert" data-pb-offers-error="1">' +
    '<p class="pb-offers-error-msg">' +
    msg +
    '</p>' +
    '<button type="button" class="builder-btn meta-btn pb-offers-retry-btn">' +
    label +
    '</button>' +
    '</div>'
  );
}

export function isOffersPluginEnabled(data) {
  const source = data || (typeof window !== 'undefined' ? window.BUILDER_DATA : null);
  return !!(source && source.navigationKernel === true);
}

/**
 * @param {object} deps
 * @param {() => boolean} deps.hasEffectiveOfferBundles
 * @param {() => string} deps.buildOffersHtml
 * @param {(opts?: object) => void} deps.bindOffersListHandlers
 * @param {() => void} deps.checkOfferValidity
 * @param {(direction: number) => boolean} deps.requestSkipEmptyOffer
 * @param {() => HTMLElement | null} [deps.getContainer]
 * @param {() => void} [deps.applyMarketFilterSync]
 * @param {() => void} [deps.prepareBeforeRender]
 * @param {(container: HTMLElement, introMode: string) => void} [deps.armOfferIntroPrehide]
 * @param {(container: HTMLElement, introMode: string) => void} [deps.maybeRunOfferIntroAfterRender]
 * @param {() => void} [deps.cancelOfferIntroTimersOnly]
 * @param {(container: HTMLElement) => void} [deps.syncBundlePreviewHeroImageFits]
 * @param {() => void} [deps.cancelOfferSelectionDeferredWork]
 * @param {() => boolean} [deps.shouldSimulateRenderFailure]
 * @param {(err: Error) => void} [deps.onRenderError]
 * @param {() => string} [deps.getOffersReadinessSignature]
 * @param {() => Promise<void>} [deps.ensureRenderReady]
 */
export function createOffersPlugin(deps) {
  let retryBound = false;

  function getContainer() {
    if (typeof deps.getContainer === 'function') return deps.getContainer();
    if (typeof document !== 'undefined') return document.getElementById('offers-list-js');
    return null;
  }

  function bindRetryHandler(container) {
    if (!container || retryBound) return;
    const btn = container.querySelector('.pb-offers-retry-btn');
    if (!btn) return;
    retryBound = true;
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      render({ intro: 'skip', _fromRetry: true });
    });
  }

  function showErrorPanel(container) {
    container.dataset.pbOffersContentHash = '';
    container.innerHTML = buildOffersErrorHtml();
    bindRetryHandler(container);
  }

  function requestSkip(direction) {
    if (typeof deps.requestSkipEmptyOffer === 'function') {
      return deps.requestSkipEmptyOffer(direction === -1 ? -1 : 1);
    }
    return false;
  }

  /**
   * @param {object} [opts]
   * @param {'auto'|'skip'} [opts.intro]
   * @param {boolean} [opts._fromSelectionDeferred]
   * @param {boolean} [opts._fromRetry]
   */
  function render(opts) {
    if (shouldSkipPluginOnChildCartEdit(CART_EDIT_FLOW, null)) {
      return;
    }

    if (typeof deps.applyMarketFilterSync === 'function') {
      deps.applyMarketFilterSync();
    }

    if (!deps.hasEffectiveOfferBundles()) {
      requestSkip(1);
      return;
    }

    const container = getContainer();
    if (!container) return;

    const runPaint = () => {
      paintOffers(opts, container);
    };

    const ensureReady =
      typeof deps.ensureRenderReady === 'function'
        ? deps.ensureRenderReady
        : async () => {
            if (areOfferRenderDepsReady()) return;
            await ensureOfferRenderDepsReady();
          };

    void ensureReady()
      .then(() => {
        if (!deps.ensureRenderReady && !areOfferRenderDepsReady()) {
          showErrorPanel(container);
          return;
        }
        runPaint();
      })
      .catch((err) => {
        if (typeof deps.onRenderError === 'function') {
          deps.onRenderError(err);
        } else if (typeof console !== 'undefined' && console.error) {
          console.error('[PB offers plugin] deps bootstrap failed', err);
        }
        showErrorPanel(container);
      });
  }

  /**
   * @param {object} [opts]
   * @param {HTMLElement} container
   */
  function paintOffers(opts, container) {
    const introMode = opts && opts.intro === 'auto' ? 'auto' : 'skip';
    const fromSelectionDeferred = !!(opts && opts._fromSelectionDeferred);
    if (!fromSelectionDeferred && typeof deps.cancelOfferSelectionDeferredWork === 'function') {
      deps.cancelOfferSelectionDeferredWork();
    }

    if (
      !opts?._fromRetry &&
      typeof deps.shouldSimulateRenderFailure === 'function' &&
      deps.shouldSimulateRenderFailure()
    ) {
      showErrorPanel(container);
      return;
    }

    try {
      if (typeof deps.prepareBeforeRender === 'function') {
        deps.prepareBeforeRender();
      }

      const newHtml = deps.buildOffersHtml();
      if (!newHtml) {
        requestSkip(1);
        return;
      }

      const newHash = offersContentHash(
        newHtml + '|' + (typeof deps.getOffersReadinessSignature === 'function' ? deps.getOffersReadinessSignature() : '')
      );
      const prevHash = container.dataset.pbOffersContentHash || '';
      const unchanged = prevHash !== '' && newHash === prevHash;

      if (unchanged) {
        deps.checkOfferValidity();
        if (typeof deps.armOfferIntroPrehide === 'function') {
          deps.armOfferIntroPrehide(container, introMode);
        }
        if (typeof deps.maybeRunOfferIntroAfterRender === 'function') {
          deps.maybeRunOfferIntroAfterRender(container, introMode);
        }
        return;
      }

      if (typeof deps.cancelOfferIntroTimersOnly === 'function') {
        deps.cancelOfferIntroTimersOnly();
      }
      container.dataset.pbOffersContentHash = newHash;
      container.innerHTML = newHtml;
      deps.bindOffersListHandlers(opts);
      if (typeof deps.syncBundlePreviewHeroImageFits === 'function') {
        deps.syncBundlePreviewHeroImageFits(container);
      }
      if (typeof deps.armOfferIntroPrehide === 'function') {
        deps.armOfferIntroPrehide(container, introMode);
      }
      deps.checkOfferValidity();
      if (typeof deps.maybeRunOfferIntroAfterRender === 'function') {
        deps.maybeRunOfferIntroAfterRender(container, introMode);
      }
    } catch (err) {
      if (typeof deps.onRenderError === 'function') {
        deps.onRenderError(err);
      } else if (typeof console !== 'undefined' && console.error) {
        console.error('[PB offers plugin]', err);
      }
      showErrorPanel(container);
    }
  }

  function onStepActive(detail) {
    if (!detail || detail.kind !== 'offer') return;
    const ctx = detail.context;
    if (!ctx || !ctx.isMain) return;
    if (shouldSkipPluginOnChildCartEdit(CART_EDIT_FLOW, ctx)) return;
    render({ intro: 'skip' });
    const container = getContainer();
    if (
      container &&
      typeof window !== 'undefined' &&
      typeof window.productBuilder?.clearOfferIntroTimers === 'function'
    ) {
      window.productBuilder.clearOfferIntroTimers();
    }
    if (container && container.classList.contains('pb-offer-intro-pending')) {
      container.classList.remove('pb-offer-intro-pending', 'pb-offer-intro-pending-hide');
      container.querySelectorAll('.pb-offer-intro-hidden').forEach((el) => {
        el.classList.remove('pb-offer-intro-hidden');
        el.removeAttribute('aria-hidden');
        el.style.opacity = '';
        el.style.transform = '';
      });
    }
  }

  function wire(lifecycle) {
    if (!lifecycle || typeof lifecycle.on !== 'function') return () => {};
    return lifecycle.on('stepActive', onStepActive);
  }

  return {
    render,
    onStepActive,
    wire,
  };
}

function attachToWindow() {
  if (typeof window === 'undefined') return;
  window.PBOffers = {
    OFFERS_ERROR_MESSAGE,
    OFFERS_RETRY_LABEL,
    offersContentHash,
    buildOffersErrorHtml,
    buildOffersHtml,
    bindOffersListHandlers,
    isOffersPluginEnabled,
    createOffersPlugin,
  };

  const pb = window.productBuilder;
  if (!pb || typeof pb._wireLifecycleStepPlugins !== 'function') return;
  pb._wireLifecycleStepPlugins();
  const container = document.getElementById('offers-list-js');
  const needsRender =
    container &&
    !container.innerHTML.trim() &&
    !container.querySelector('[data-pb-offers-error]');
  if (needsRender && typeof pb._emitStepActive === 'function') {
    pb._emitStepActive();
  }
}

attachToWindow();
